import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * Reports & Analytics API
 * Handles booking loans and fetching analytics data
 */

// ==================== BOOKING ====================

/**
 * Book a funded loan (mark as finalized for reporting)
 * POST /api/cases/{caseId}/book
 */
export async function bookLoan(caseId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
        logError('Failed to book loan', response.status);
        const error = new Error('Failed to book loan');
        error.status = response.status;
        throw error;
    }
    return response.json();
}

/**
 * Unbook a loan (admin only - requires reason)
 * DELETE /api/cases/{caseId}/book
 */
export async function unbookLoan(caseId, reason) {
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${caseId}/book`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
    });
    if (!response.ok) {
        logError('Failed to unbook loan', response.status);
        throw new Error('Failed to unbook loan');
    }
    return response.json();
}

// ==================== ANALYTICS ====================

/**
 * Fetch production report for a date range
 * GET /api/reports/production?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 */
export async function fetchProductionReport(startDate, endDate) {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/production?${params}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch production report', response.status);
        throw new Error('Failed to fetch production report');
    }
    return response.json();
}

/**
 * Fetch pipeline snapshot for a date range
 * GET /api/reports/pipeline?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 */
export async function fetchPipelineReport(startDate, endDate) {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/pipeline?${params}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch pipeline report', response.status);
        throw new Error('Failed to fetch pipeline report');
    }
    return response.json();
}

/**
 * Fetch funnel/conversion metrics
 * GET /api/reports/funnel?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 */
export async function fetchFunnelReport(startDate, endDate) {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/funnel?${params}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch funnel report', response.status);
        throw new Error('Failed to fetch funnel report');
    }
    return response.json();
}

/**
 * Fetch loan officer performance metrics
 * GET /api/reports/loan-officers?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 */
export async function fetchLoanOfficerReport(startDate, endDate) {
    const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/loan-officers?${params}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch loan officer report', response.status);
        throw new Error('Failed to fetch loan officer report');
    }
    return response.json();
}

/**
 * Fetch booked loans for a date range
 * GET /api/reports/booked?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD
 */
export async function fetchBookedLoans(startDate, endDate, params = {}) {
    const queryParams = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
    });
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.loan_type) queryParams.append('loan_type', params.loan_type);
    if (params.loan_officer) queryParams.append('loan_officer', params.loan_officer);

    const response = await fetchWithAuth(`${BASE_URL}/api/reports/booked?${queryParams}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch booked loans', response.status);
        throw new Error('Failed to fetch booked loans');
    }
    return response.json();
}

/**
 * Export report to PDF/CSV
 * POST /api/reports/export
 */
export async function exportReport(reportType, startDate, endDate, format = 'pdf') {
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            report_type: reportType,
            start_date: startDate,
            end_date: endDate,
            format: format
        })
    });
    if (!response.ok) {
        logError('Failed to export report', response.status);
        throw new Error('Failed to export report');
    }
    // Return blob for download
    return response.blob();
}

// ==================== PERIOD MANAGEMENT ====================

/**
 * List reporting periods
 * GET /api/reports/periods
 */
export async function fetchReportingPeriods(year) {
    const params = year ? `?year=${year}` : '';
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/periods${params}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch reporting periods', response.status);
        throw new Error('Failed to fetch reporting periods');
    }
    return response.json();
}

/**
 * Close a reporting period
 * POST /api/reports/periods/{periodId}/close
 */
export async function closeReportingPeriod(periodId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/periods/${periodId}/close`, {
        method: 'POST'
    });
    if (!response.ok) {
        logError('Failed to close reporting period', response.status);
        throw new Error('Failed to close reporting period');
    }
    return response.json();
}

/**
 * Reopen a reporting period (admin only)
 * POST /api/reports/periods/{periodId}/reopen
 */
export async function reopenReportingPeriod(periodId, reason) {
    const response = await fetchWithAuth(`${BASE_URL}/api/reports/periods/${periodId}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
    });
    if (!response.ok) {
        logError('Failed to reopen reporting period', response.status);
        throw new Error('Failed to reopen reporting period');
    }
    return response.json();
}
